import {Breadcrumb, Button, Col, Form, InputGroup, Row} from 'react-bootstrap'
import {FormattedMessage, defineMessages, useIntl} from 'react-intl'
import {useCallback, useEffect, useState} from 'react'
import moment from 'moment'
import {toast} from 'react-toastify'
import {useLazyQuery} from '@apollo/client'
import {GET_ALL_PIPELINES} from '../../gql/queries/pipelines'
import {IResPipelines} from '../../types/pipelines'
import {parseId} from '../../helpers'
import {useDisclourse} from '../../hooks/useDisclourse'
import {ModalAddPipeline} from './components/ModalAddPipeline'
import {ModalDuplicatePipeline} from './components/ModalDuplicatePipeline'
import {ModalDeletePipeline} from './components/ModalDeletePipeline'
import {ModalEditPipeline} from './components/ModalEditPipeline'
import {cleanVariables, PaginationUtils} from '../shared/PaginationUtils'
import {SIZE_PAGE_DEFAULT} from '../../helpers/consts'
import {PaginationSection} from '../shared/PaginationSection'
import {GET_ALL_ORGANIZATIONS} from '../../gql/queries/organizationsQuery'
import {useGetUser} from '../../store/userStore'
import {useQuery} from '@apollo/client'

const pM = defineMessages({
  leadPipelines: {id: 'leads.pipelines.leadPipelines', defaultMessage: 'Lead Pipelines'},
  newPipeline: {id: 'leads.pipelines.newPipeline', defaultMessage: 'New Pipeline'},
  allPipelines: {id: 'leads.pipelines.allPipelines', defaultMessage: 'All Pipelines'},
  searchPlaceholder: {id: 'leads.pipelines.searchPlaceholder', defaultMessage: 'Search by pipeline name...'},
  thPipelineName: {id: 'leads.pipelines.thPipelineName', defaultMessage: 'Pipeline Name'},
  thOrganization: {id: 'leads.pipelines.thOrganization', defaultMessage: 'Organization'},
  thStages: {id: 'leads.pipelines.thStages', defaultMessage: 'Stages'},
  noOrganization: {id: 'leads.pipelines.noOrganization', defaultMessage: 'No organization'},
  noStages: {id: 'leads.pipelines.noStages', defaultMessage: 'No stages'},
  duplicateTooltip: {id: 'leads.pipelines.duplicateTooltip', defaultMessage: 'Duplicate Pipeline'},
  deleteTooltip: {id: 'leads.pipelines.deleteTooltip', defaultMessage: 'Delete Pipeline'},
})

export const PipelinesPage = () => {
  const intl = useIntl()
  const errMsg = (message?: string) =>
    intl.formatMessage({id: 'common.error.withMessage', defaultMessage: 'Error: {message}'}, {message})

  const [gqlGetPipelines, {data, error, refetch}] = useLazyQuery<IResPipelines>(
    GET_ALL_PIPELINES,
    {
      fetchPolicy: 'no-cache',
    }
  )

  const [dataArr, setDataArr] = useState<IResPipelines['allPipelines']['edges']>([])

  const [infoEdit, setInfoEdit] = useState<null | undefined | {id: string; name: string; organization?: {id: string; name: string}}>(null)
  const userGroups = useGetUser((s) => s.user?.groups) || []
  const isSuperAdmin = userGroups.includes('SuperAdministrador')
  const [organizationId, setOrganizationId] = useState<string>('')

  const {isOpen, onClose, onOpen} = useDisclourse()
  const {
    isOpen: isOpenDuplicate,
    onClose: onCloseDuplicate,
    onOpen: onOpenDuplicate,
  } = useDisclourse()
  const {
    isOpen: isOpenDeletePipe,
    onClose: onCloseDeletePipe,
    onOpen: onOpenDeletePipe,
  } = useDisclourse()
  const {
    isOpen: isOpenEditPipe,
    onClose: onCloseEditPipe,
    onOpen: onOpenEditPipe,
  } = useDisclourse()

  const [first, setFirst] = useState(SIZE_PAGE_DEFAULT)
  const [currentPage, setCurrentPage] = useState(1)

  let totalElements = data?.allPipelines?.totalCount ?? 0
  const totalPages = Math.ceil(totalElements / first)
  const {start, end} = PaginationUtils.getPaginationRange(currentPage, first, totalElements)

  const [formData, setFormData] = useState({
    text: '',
  })

  const updatePage = (n: number) => {
    setCurrentPage(n)
  }

  const handleFilters = useCallback(() => {
    const variables = cleanVariables({
      first,
      offset: (currentPage - 1) * first,
      nameIcontains: formData.text,
      organization: isSuperAdmin && organizationId ? parseId(organizationId) : null,
    })

    gqlGetPipelines({variables})
  }, [currentPage, first, formData.text, gqlGetPipelines, isSuperAdmin, organizationId])

  useEffect(() => {
    handleFilters()
  }, [handleFilters])

  useEffect(() => {
    if (!data) return
    const {edges} = data.allPipelines
    setDataArr(edges)
  }, [data])

  useEffect(() => {
    if (error) {
      toast.error(errMsg(error.message))
    }
  }, [error])

  const {
    data: dataOrganizations,
    loading: loadingOrganizations,
    error: errorOrganizations,
  } = useQuery(GET_ALL_ORGANIZATIONS, {
    variables: {first: 100},
    fetchPolicy: 'no-cache',
    skip: !isSuperAdmin,
  })

  useEffect(() => {
    if (errorOrganizations) toast.error(errMsg(errorOrganizations.message))
  }, [errorOrganizations])

  return (
    <div className='policies-page'>
      <header className='header'>
        <Breadcrumb>
          <Breadcrumb.Item className='d-blok d-md-none'>
            <span>
              <i className='bi bi-list fs-1' />
            </span>
          </Breadcrumb.Item>
          <Breadcrumb.Item>{intl.formatMessage(pM.leadPipelines)}</Breadcrumb.Item>
        </Breadcrumb>
        <div className='d-flex align-items-center gap-2'>
          <Button
            size='sm'
            onClick={() => {
              onOpen()
              setInfoEdit(null)
            }}
          >
            <i className='bi bi-plus fs-3' /> {intl.formatMessage(pM.newPipeline)}
          </Button>
        </div>
      </header>
      <Row className='policies'>
        <Col md={12} lg={12} sm={12} xl={12} className='container'>
          <div className='policies-container'>
            <div className='title-header'>
              <h3>{intl.formatMessage(pM.allPipelines)}</h3>
            </div>
            <div className='filters-header'>
              <InputGroup>
                <Form.Control
                  placeholder={intl.formatMessage(pM.searchPlaceholder)}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      text: e.target.value,
                    }))
                  }
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      handleFilters()
                    }
                  }}
                  value={formData.text}
                />
                {isSuperAdmin && (
                  <Form.Select
                    value={organizationId}
                    onChange={(e) => {
                      setOrganizationId(e.target.value)
                      setCurrentPage(1)
                    }}
                    disabled={loadingOrganizations}
                    style={{minHeight: '38px'}}
                  >
                    {loadingOrganizations ? (
                      <option value=''>{intl.formatMessage({id: 'common.loading', defaultMessage: 'Loading...'})}</option>
                    ) : (
                      <option value=''>{intl.formatMessage({id: 'leads.page.allOrganizations', defaultMessage: 'All Organizations'})}</option>
                    )}
                    {dataOrganizations?.allOrganizations?.edges?.map(({node}: any) => (
                      <option key={node.id} value={parseId(node.id)}>
                        {node.name}
                      </option>
                    ))}
                  </Form.Select>
                )}
                <Button
                  onClick={() => handleFilters()}
                  variant='outline-success'
                  style={{border: '1px solid #dbdfe9'}}
                >
                  <FormattedMessage id='common.action.search' defaultMessage='Search' />
                </Button>
              </InputGroup>
            </div>
            <div className='results-header'>
              <PaginationSection
                first={first}
                setFirst={(n) => {
                  setFirst(n)
                  setCurrentPage(1)
                }}
                currentPage={currentPage}
                totalPages={totalPages}
                start={start}
                end={end}
                totalPolicies={totalElements}
                updatePage={updatePage}
                PaginationUtils={PaginationUtils}
              />
            </div>
            <div className='list-carrier table-scrollable table-container'>
              <table className='table-scrollable table-scroll table table-striped table-hover'>
                <thead>
                  <tr role='row'>
                    <th className='fw-bold'>{intl.formatMessage(pM.thPipelineName)}</th>
                    {isSuperAdmin && <th className='fw-bold d-none d-lg-table-cell'>{intl.formatMessage(pM.thOrganization)}</th>}
                    <th className='fw-bold d-none d-lg-table-cell'>{intl.formatMessage(pM.thStages)}</th>
                    <th className='fw-bold d-none d-lg-table-cell'><FormattedMessage id='common.field.created' defaultMessage='Created' /></th>
                    <th className='fw-bold d-none d-lg-table-cell text-center'><FormattedMessage id='common.field.actions' defaultMessage='Actions' /></th>
                  </tr>
                </thead>
                <tbody>
                  {dataArr.map(({node}) => (
                    <tr key={node.id}>
                      <td className='px-4'>{node.name}</td>
                      {isSuperAdmin && (
                        <td className='px-2 d-none d-lg-table-cell'>
                          {node.organization ? node.organization.name : <span className='text-muted'>{intl.formatMessage(pM.noOrganization)}</span>}
                        </td>
                      )}
                      <td className='px-2 d-none d-lg-table-cell' style={{maxWidth: '45vw'}}>
                        <div
                          className='d-flex align-items-center gap-2'
                          style={{
                            flexWrap: 'wrap',
                            rowGap: '6px',
                          }}
                        >
                          {node.stagePipelineSet.edges.length > 0 ? (
                            node.stagePipelineSet.edges
                              .sort((a, b) => a.node.order - b.node.order)
                              .map(({node: nodeStage}, i) => (
                                <span
                                  key={nodeStage.id}
                                  className='d-inline-flex align-items-center gap-2'
                                >
                                  <span className='badge bg-primary text-white'>{nodeStage.name}</span>
                                  {i + 1 !== node.stagePipelineSet.edges.length && (
                                    <i className='bi bi-arrow-right text-dark' />
                                  )}
                                </span>
                              ))
                          ) : (
                            <span className='text-muted'>
                              <i className='bi bi-exclamation-circle' /> {intl.formatMessage(pM.noStages)}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className='px-2 d-none d-lg-table-cell'>
                        {moment(node.created).format('DD MMM, YYYY')}
                      </td>
                      <td className='px-2 text-center d-none d-lg-table-cell'>
                        <div className='d-flex align-items-center justify-content-center gap-2'>
                          <Button
                            size='sm'
                            variant='outline-primary'
                            onClick={() => {
                              setInfoEdit({id: node.id, name: node.name, organization: node.organization})
                              onOpenEditPipe()
                            }}
                          >
                            <i className='bi bi-pencil-square' /> <FormattedMessage id='common.action.edit' defaultMessage='Edit' />
                          </Button>
                          <button
                            title={intl.formatMessage(pM.duplicateTooltip)}
                            style={{border: 0, backgroundColor: 'transparent', padding: '4px 8px'}}
                            onClick={() => {
                              onOpenDuplicate()
                              setInfoEdit({id: node.id, name: node.name})
                            }}
                          >
                            <i className='bi bi-clipboard text-info fs-5' />
                          </button>
                          <button
                            title={intl.formatMessage(pM.deleteTooltip)}
                            style={{border: 0, backgroundColor: 'transparent', padding: '4px 8px'}}
                            onClick={() => {
                              onOpenDeletePipe()
                              setInfoEdit({id: node.id, name: node.name})
                            }}
                          >
                            <i className='bi bi-trash text-danger fs-5' />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Col>
      </Row>

      {isOpen && (
        <ModalAddPipeline isOpen={isOpen} onClose={onClose} refetch={refetch} infoEdit={infoEdit} />
      )}

      {isOpenDuplicate && infoEdit && (
        <ModalDuplicatePipeline
          isOpen={isOpenDuplicate}
          onClose={onCloseDuplicate}
          refetch={refetch}
          infoEdit={infoEdit}
        />
      )}

      {isOpenDeletePipe && infoEdit && (
        <ModalDeletePipeline
          isOpen={isOpenDeletePipe}
          onClose={onCloseDeletePipe}
          refetch={refetch}
          infoEdit={infoEdit}
        />
      )}

      {isOpenEditPipe && infoEdit && (
        <ModalEditPipeline
          isOpen={isOpenEditPipe}
          onClose={onCloseEditPipe}
          refetch={refetch}
          pipeline={infoEdit}
        />
      )}
    </div>
  )
}
